/* TTJ scroll reveal (site-wide, IntersectionObserver based, no scroll listeners).
 *
 * - Reuses the theme's existing system: sections that already use .scroll-trigger / .animate--slide-in
 *   (animations.js) are left alone, so nothing animates twice.
 * - Adds the same fade / fade-up to everything else:
 *     a) every top-level section in #MainContent that has no existing reveal (auto)
 *     b) any element with the attribute data-ttj-reveal (opt-in for future sections/blocks)
 *     c) data-ttj-reveal="off" excludes an element or section.
 * - Each element animates once, the first time it enters the viewport (starts slightly before it is fully in view).
 * - Does nothing with prefers-reduced-motion: reduce, without IntersectionObserver, or in the Theme Editor
 *   (so content is never hidden there). Sticky/fixed elements and header, dialogs, drawers, carousels' own UI
 *   and the PDP sticky Product Details are never animated.
 * - Master switch and style/duration/distance: Theme settings > Animations (CSS custom properties, see theme.liquid).
 */
(function () {
  if (window.ttjScrollReveal) return;

  var STATE = 'data-ttj-reveal-state';
  var SKIP_AUTO_IDS = /(announcement|header|footer|main-cart|cart-drawer|password)/i;
  var NEVER_INSIDE =
    'header, footer, sticky-header, cart-drawer, cart-notification, dialog, modal-dialog, [role="dialog"], ' +
    '.announcement-bar, .shopify-section-group-header-group, .ttj-product-details, .product__column-sticky, ' +
    '[data-ttj-reveal="off"]';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supported = 'IntersectionObserver' in window && !reducedMotion.matches && !(window.Shopify && Shopify.designMode);
  var observer = null;

  function isPositioned(el) {
    var p = window.getComputedStyle(el).position;
    return p === 'sticky' || p === 'fixed';
  }

  function eligible(el) {
    if (!el || el.hasAttribute(STATE)) return false;
    if (el.closest(NEVER_INSIDE)) return false;
    if (isPositioned(el)) return false;
    return true;
  }

  function duration(el) {
    var v = window.getComputedStyle(el).getPropertyValue('--ttj-reveal-duration').trim();
    var n = parseFloat(v);
    if (isNaN(n)) return 600;
    return /ms$/.test(v) ? n : /s$/.test(v) ? n * 1000 : n;
  }

  function finish(el) {
    el.removeAttribute(STATE);
  }

  function reveal(el) {
    if (!observer) return;
    observer.unobserve(el);
    if (el.getAttribute(STATE) !== 'hidden') return;
    el.setAttribute(STATE, 'shown');
    var done = false;
    var end = function () {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', onEnd);
      finish(el);
    };
    var onEnd = function (e) {
      if (e.target === el && e.propertyName === 'opacity') end();
    };
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(end, duration(el) + 150);
  }

  function onIntersect(entries) {
    entries.forEach(function (entry) {
      /* Reveal when entering, or when already scrolled past (fast scroll / restored scroll position). */
      if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) reveal(entry.target);
    });
  }

  function collect(root) {
    var list = [];
    var scope = root || document;

    /* a) auto: top-level sections with no existing reveal */
    var main = document.getElementById('MainContent');
    if (main) {
      Array.prototype.forEach.call(main.children, function (section) {
        if (!section.classList || !section.classList.contains('shopify-section')) return;
        if (root && !root.contains(section) && root !== section) return;
        if (SKIP_AUTO_IDS.test(section.id || '')) return;
        if (section.hasAttribute('data-ttj-reveal')) return;
        if (section.querySelector('.scroll-trigger, [data-ttj-reveal]')) return;
        list.push(section);
      });
    }

    /* b) explicit opt-in */
    if (scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-ttj-reveal]:not([data-ttj-reveal="off"])'), function (el) {
        list.push(el);
      });
    }
    return list;
  }

  function init(root) {
    if (!supported) return;
    if (!observer) {
      observer = new IntersectionObserver(onIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    }
    var targets = collect(root).filter(eligible);
    targets.forEach(function (el) {
      el.setAttribute(STATE, 'hidden');
    });
    /* Observe on the next frame so the hidden state is painted first and elements already in view fade in. */
    window.requestAnimationFrame(function () {
      targets.forEach(function (el) {
        if (el.getAttribute(STATE) === 'hidden') observer.observe(el);
      });
    });
  }

  window.ttjScrollReveal = { refresh: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
